import { onActivated, onMounted, provide, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useFocus, useInfiniteScroll, useScroll } from '@vueuse/core'
import type { HomePageConfig } from '../configs/types'
import { api } from '@/api'
import { COLUMN_TYPE } from '@/constants'
import { useBannerShow } from '@/composables/useBannerShow'
import { directPageTimeReport, useElementClick } from '@/composables/tracks'

/**
 * Home 页面业务逻辑
 */
export function useHomePage(_config: HomePageConfig) {
  const router = useRouter()
  const route = useRoute()
  const { elementClick } = useElementClick()

  // ficmoon 背景
  const ficMoonHomeBgUrl = ref('')

  const pageList = ref<any[]>([])
  const pageInfo = ref<any>({})

  const skeleton = ref(false)
  async function getPageInfo() {
    skeleton.value = true
    try {
      const { data } = await api.post('/page/index')
      if (data.page && data.page.length === 0)
        return

      let isPageId = false
      let pageId: string = Array.isArray(route.query.pageId)
        ? route.query.pageId[0] || ''
        : route.query.pageId || ''
      for (const item of data.page) {
        item.isLoad = false
        if (pageId === item.id)
          isPageId = true

        pageList.value.push(item)
      }
      if (!isPageId)
        pageId = pageList.value[0].id || ''

      if (pageId)
        await switchNav(pageList.value[0])
    }
    catch (error: any) {
      console.error(error)
    }
    finally {
      skeleton.value = false
    }
  }

  // Feed
  const feeds = ref<any[]>([])
  const feedShowName = ref('')
  const feedId = ref('')
  const feedsColumnType = ref('')
  const feedFetching = ref(false)
  const feedEnding = ref(false)
  const feedError = ref<any>()
  function initFeed() {
    const module: any = pageInfo.value.modules.find((item: any) => item.columnType === COLUMN_TYPE.INFO_FLOW)
    if (module) {
      feedShowName.value = module.showName
      feedId.value = module.id
      feedsColumnType.value = module.columnType
      feeds.value = module.resourceList.records
    }
    else {
      feedShowName.value = ''
      feedId.value = ''
      feedsColumnType.value = ''
      feeds.value = []
    }
    feedFetching.value = false
    feedEnding.value = false
    feedError.value = null
  }
  async function fetchFeed() {
    if (!pageInfo.value?.modules)
      return

    const module: any = pageInfo.value.modules.find((item: any) => item.columnType === COLUMN_TYPE.INFO_FLOW)
    if (!module) {
      feeds.value = []
      return
    }
    if (module.resourceList.current >= module.resourceList.pages) {
      feedEnding.value = true
      return
    }
    feedFetching.value = true
    try {
      const { data } = await api.get<any, any>('/content/getColumnContentList', {
        params: {
          columnId: module.id,
          pageIndex: module.resourceList.current + 1,
          pageSize: module.resourceList.size,
        },
      })
      module.resourceList.current = data.resourceList.current
      module.resourceList.records = module.resourceList.records.concat(data.resourceList.records)
      feeds.value = module.resourceList.records
    }
    catch (error) {
      feedError.value = error
    }
    finally {
      feedFetching.value = false
    }
  }

  const infiniteRef = ref<HTMLElement>()
  provide('pageRef', infiniteRef)

  // 设置滚动容器的函数,并添加滚动事件监听
  function setInfiniteRef(el: HTMLElement | null) {
    if (el)
      infiniteRef.value = el
  }

  useInfiniteScroll(infiniteRef,
    async () => {
      if (feedFetching.value || feedEnding.value || feedError.value)
        return
      await fetchFeed()
    },
    { distance: 50 },
  )

  // switch navigation
  const modules = ref<any[]>([])
  const currentTagId = ref<string>('')
  const enterTabTime = ref<number>(Date.now())

  function switchNavTrack(item: any) {
    elementClick(item.showName)
    const leaveTabTime = Date.now()
    const duration = leaveTabTime - enterTabTime.value
    directPageTimeReport(item.showName, duration, pageInfo.value.showName)
    switchNav(item)
  }
  async function switchNav(item: any) {
    const pageId = item.id
    pageInfo.value = pageList.value.find(item => item.id === pageId)
    if (pageInfo.value.isLoad) {
      modules.value = pageInfo.value.modules
      // 初始化 currentTagId
      modules.value.forEach((item) => {
        if (item.columnType === 6)
          currentTagId.value = item.tagList[0].id
      })
      // 提取第一个有效的封面图作为背景图
      const coverUrl = extractFirstCoverUrl(modules.value)
      if (coverUrl)
        ficMoonHomeBgUrl.value = coverUrl
      initFeed()
      return
    }

    try {
      const { data } = await api.get(`/page/pageDetail?pageId=${pageId}`)
      pageInfo.value.modules = (data ?? []).filter((item: any) => {
        return item.resourceList?.records?.length > 0 || item.tagList?.length > 0
      })

      // 提取第一个有效的封面图作为背景图
      const coverUrl = extractFirstCoverUrl(data)
      if (coverUrl)
        ficMoonHomeBgUrl.value = coverUrl

      modules.value = pageInfo.value.modules
      // 初始化 currentTagId
      modules.value.forEach((item) => {
        if (item.columnType === 6)
          currentTagId.value = item.tagList[0].id
      })
      initFeed()
    }
    catch (error: any) {
      console.error(error)
    }
    finally {
      pageInfo.value.isLoad = true
    }
  }

  /**
   * 从接口数据中提取第一个有效的 contentCoverUrl
   * 会遍历 tagList 和 resourceList 中的所有书籍
   * @param data 接口返回的数据
   * @returns 第一个有效的 contentCoverUrl,如果没有则返回空字符串
   */
  function extractFirstCoverUrl(data: any): string {
    if (!data || !Array.isArray(data))
      return ''

    for (const column of data) {
      // 检查 tagList
      if (column.tagList && Array.isArray(column.tagList)) {
        for (const tag of column.tagList) {
          if (tag.resourceList?.records && Array.isArray(tag.resourceList.records)) {
            for (const book of tag.resourceList.records) {
              if (book.contentCoverUrl && book.contentCoverUrl.trim())
                return book.contentCoverUrl
            }
          }
        }
      }

      // 检查 resourceList
      if (column.resourceList?.records && Array.isArray(column.resourceList.records)) {
        for (const book of column.resourceList.records) {
          if (book.contentCoverUrl && book.contentCoverUrl.trim())
            return book.contentCoverUrl
        }
      }
    }

    return ''
  }

  // function
  onMounted(() => {
    getPageInfo()
    useBannerShow()
  })

  const inputRef = ref<HTMLInputElement | null>(null)
  const input = ref('')
  const { focused: inputFocused } = useFocus(inputRef)
  const enterSearchTime = ref<number>(Date.now())

  watch(
    () => inputFocused.value,
    (val: boolean) => {
      if (val)
        enterSearchTime.value = Date.now()
    },
  )

  function onSearch() {
    inputRef.value?.blur()
    router.push({ name: 'search', query: { kw: input.value, st: String(enterSearchTime.value) } })
  }

  // 设置 input ref 的函数,供子组件调用
  function setInputRef(el: any) {
    inputRef.value = el
  }

  // 切换页面, 回到原来位置
  const { y } = useScroll(infiniteRef)
  onActivated(() => {
    infiniteRef.value?.scrollTo(0, y.value)
    input.value = ''
    inputRef.value?.blur()

    // 恢复滚动位置
    // setTimeout(() => {
    //   if (infiniteRef.value && savedScrollTop.value > 0)
    //     infiniteRef.value.scrollTop = savedScrollTop.value
    // }, 0)
  })

  function gotoCollection(id: string) {
    elementClick(`seeall_${id}`)
    router.push({ name: 'collection', params: { columnId: id } })
  }
  function gotoTags(id: string) {
    router.push({
      name: 'tags',
      query: { tagId: id },
    })
  }
  function gotoBook(id: string) {
    router.push({
      name: 'book',
      params: { contentId: id },
    })
  }

  function getCurrentTagResources(col: any) {
    const targetTag = col.tagList.find((tag: any) => tag.id === currentTagId.value)
    if (targetTag.resourceList?.records && targetTag.resourceList.records.length && targetTag.resourceList.records.length > 6)
      return targetTag.resourceList?.records.slice(0, 6)

    return targetTag.resourceList?.records || []
  }

  function getCurrentShowTags(col: any) {
    if (col.tagList && col.tagList.length) {
      return col.tagList
        .filter((tag: any) => tag.resourceList.records && tag.resourceList.records.length > 0)
    }
    return []
  }

  function handleTagClick(tagId: string) {
    currentTagId.value = tagId
  }

  return {
    // refs
    infiniteRef,
    inputRef,
    input,
    inputFocused,
    pageList,
    pageInfo,
    skeleton,
    modules,
    currentTagId,
    feeds,
    feedShowName,
    feedId,
    feedsColumnType,
    feedFetching,
    feedEnding,
    homeBgUrl: ficMoonHomeBgUrl,

    // methods
    setInfiniteRef,
    switchNavTrack,
    onSearch,
    gotoCollection,
    gotoTags,
    gotoBook,
    getCurrentTagResources,
    getCurrentShowTags,
    handleTagClick,
    setInputRef,
  }
}
